import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Save, Send, X, CheckCircle, AlertTriangle, BookOpen, Printer,
} from 'lucide-react';
import {
  journalAssessmentService,
  JournalAssignmentDetail,
  CreateUpdateJournalAssessmentPayload,
} from '../services/adminAuth';
import { useNotification } from '../../sharedComponents/Notifications';

const JOURNAL_APP_EVAL_PRINT_STYLES = `
@media print {
    @page { size: A4; margin: 14mm 12mm; }
    body, html { margin: 0 !important; padding: 0 !important; background: white !important; }
    aside, nav { display: none !important; }
    .fixed { display: none !important; }
    .w-full.max-w-6xl { max-width: 100% !important; margin: 0 !important; padding: 0 !important; }
    .jae-print-doc, .jae-print-doc * {
        color: #000 !important;
        background: transparent !important;
        box-shadow: none !important;
    }
    .jae-print-doc { background: white !important; }
    .jae-print-section { page-break-inside: avoid; break-inside: avoid; }
    h1, h2, h3, h4 { break-after: avoid; page-break-after: avoid; }
}
`;

// ── Constants ──────────────────────────────────────────────────────────────────

const ZERO_GUID = '00000000-0000-0000-0000-000000000000';
const SECTION_NUMBER = 0;

/** Max raw scores per criterion (used for payload & display only) — sums to 100 */
const CRITERIA_MAX: Record<string, number> = {
  DATE_COMMENCEMENT:          10,
  COPIES_SUBMITTED:            3,
  EDITORIAL_BOARD_IDENTIFIABLE: 5,
  EDITORIAL_BOARD_QUALIFICATION: 15,
  EDITOR_IN_CHIEF_VISIBILITY:  2,
  ISSN_STATUS:                 10,
  PEER_REVIEW:                 5,
  INDEXING:                   15,
  PUBLICATION_CONSISTENCY:    10,
  EDITORIAL_POLICY:           10,
  AUTHOR_SPREAD:              10,
  DOI:                         5,
};

/** Human-readable label for validation messages */
const FIELD_LABELS: Record<string, string> = {
  DATE_COMMENCEMENT:           'Date of Commencement',
  COPIES_SUBMITTED:            'No. of Copies Submitted',
  EDITORIAL_BOARD_IDENTIFIABLE:'Identifiable Editorial Board',
  EDITORIAL_BOARD_QUALIFICATION:'Qualification & Affiliation',
  EDITOR_IN_CHIEF_VISIBILITY:  'Visibility of Editor-in-Chief',
  ISSN_STATUS:                 'ISSN',
  PEER_REVIEW:                 'Evidence of Peer Review Mechanism',
  INDEXING:                    'Evidence of Abstracting & Indexing',
  PUBLICATION_CONSISTENCY:     'Consistency of Publication',
  EDITORIAL_POLICY:            'Editorial Policy',
  AUTHOR_SPREAD:               'Spread of Contributing Authors',
  DOI:                         'Evidence of Digital Object Identifier (DOI)',
};

/** Option label map — value → display label (for selectedValue in payload) */
const OPTION_LABELS: Record<string, Record<number, string>> = {
  DATE_COMMENCEMENT:           { 1: '1 pt/yr', 10: 'Max 10 pts' },
  COPIES_SUBMITTED:            { 1: '1 pt/vol', 3: 'Max 3 pts (≥3 vols)' },
  EDITORIAL_BOARD_IDENTIFIABLE:{ 0: 'Not identified', 5: 'Board identified' },
  EDITORIAL_BOARD_QUALIFICATION:{ 3: '<50% PhD', 5: '≥50% PhD' },
  EDITOR_IN_CHIEF_VISIBILITY:  { 2: 'Google Scholar/Scopus/ResearchGate etc.' },
  ISSN_STATUS:                 { 5: 'Print only', 10: 'Print & Online' },
  PEER_REVIEW:                 { 5: 'Has peer review evidence' },
  INDEXING:                    { 2: '1 platform', 10: '2 platforms', 15: '≥3 platforms' },
  PUBLICATION_CONSISTENCY:     { 10: 'Consistent with stated frequency' },
  EDITORIAL_POLICY:            { 10: 'Has editorial policy' },
  AUTHOR_SPREAD:               { 5: 'Local', 7: 'National', 10: 'International' },
  DOI:                         { 5: 'Has DOI' },
};

type ScoreMap = Record<string, number | null>;

const emptyScoreMap = (): ScoreMap =>
  Object.fromEntries(Object.keys(CRITERIA_MAX).map(k => [k, null]));

const totalMax = Object.values(CRITERIA_MAX).reduce((a, b) => a + b, 0); // 100

// ── Shared UI Components ───────────────────────────────────────────────────────

interface ROFieldProps { label: string; value?: string | number | null; fullWidth?: boolean; }
const ROField: React.FC<ROFieldProps> = ({ label, value, fullWidth = false }) => (
  <div className={fullWidth ? 'col-span-full' : ''}>
    <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1">{label}</p>
    <div className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-sm font-medium text-gray-800 min-h-[38px]">
      {value ? value : <span className="italic text-gray-400">—</span>}
    </div>
  </div>
);

interface SectionCardProps { title?: string; accent?: boolean; children: React.ReactNode; }
const SectionCard: React.FC<SectionCardProps> = ({ title, accent = false, children }) => (
  <div className={`rounded-xl border p-5 space-y-4 ${accent ? 'border-emerald-200 bg-emerald-50/40' : 'border-gray-100 bg-white'}`}>
    {title && (
      <div className={`flex items-center gap-2 pb-3 border-b ${accent ? 'border-emerald-200' : 'border-gray-100'}`}>
        <div className={`w-1 h-4 rounded-full ${accent ? 'bg-[#088A61]' : 'bg-gray-300'}`} />
        <h3 className={`text-[11px] font-black uppercase tracking-widest ${accent ? 'text-[#088A61]' : 'text-gray-500'}`}>{title}</h3>
      </div>
    )}
    {children}
  </div>
);

interface ScoreOptionsProps {
  label: string;
  maxPts: number;
  options: Array<{ label: string; value: number }>;
  selected: number | null;
  onChange: (v: number | null) => void;
  readOnly?: boolean;
  hint?: string;
}
const ScoreOptions: React.FC<ScoreOptionsProps> = ({ label, maxPts, options, selected, onChange, readOnly = false, hint }) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider">{label}</p>
      <span className="text-[10px] font-bold text-[#088A61] bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
        Max {maxPts} pts
      </span>
    </div>
    {hint && <p className="text-[10px] text-gray-400 italic mb-2">{hint}</p>}
    <div className="flex flex-wrap gap-2">
      {options.map(opt => {
        const isSel = selected === opt.value;
        return (
          <button
            key={opt.label}
            type="button"
            disabled={readOnly}
            onClick={() => !readOnly && onChange(isSel ? null : opt.value)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-bold transition-all ${
              isSel
                ? 'bg-[#088A61] border-[#088A61] text-white shadow-sm'
                : 'bg-white border-gray-200 text-gray-600 hover:border-[#088A61] hover:text-[#088A61]'
            } ${readOnly ? 'cursor-default opacity-70' : 'cursor-pointer'}`}
          >
            <span>{opt.label}</span>
            <span className={`text-[10px] font-black rounded px-1 ${isSel ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
              {opt.value} pt{opt.value !== 1 ? 's' : ''}
            </span>
          </button>
        );
      })}
    </div>
    {selected !== null && selected !== undefined && (
      <div className="mt-2 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5">
        <CheckCircle className="w-3 h-3 text-[#088A61]" />
        <span className="text-xs font-bold text-[#088A61]">Selected: {selected} points</span>
      </div>
    )}
  </div>
);

// ── Numeric input for graduated scores (date commencement & copies submitted) ──
interface NumericScoreProps {
  label: string;
  maxPts: number;
  value: number | null;
  onChange: (v: number | null) => void;
  readOnly?: boolean;
  hint?: string;
}
const NumericScore: React.FC<NumericScoreProps> = ({ label, maxPts, value, onChange, readOnly = false, hint }) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider">{label}</p>
      <span className="text-[10px] font-bold text-[#088A61] bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
        Max {maxPts} pts
      </span>
    </div>
    {hint && <p className="text-[10px] text-gray-400 italic mb-2">{hint}</p>}
    <div className="flex items-center gap-3">
      <input
        type="number"
        min={0}
        max={maxPts}
        disabled={readOnly}
        value={value ?? ''}
        onChange={e => {
          const v = e.target.value === '' ? null : Math.min(maxPts, Math.max(0, Number(e.target.value)));
          onChange(v);
        }}
        className="w-28 px-3 py-2 border border-gray-200 rounded-lg text-sm font-bold text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#088A61]/30 focus:border-[#088A61] disabled:bg-gray-100 disabled:cursor-default"
        placeholder="0"
      />
      <span className="text-xs text-gray-400 font-medium">/ {maxPts} pts</span>
    </div>
    {value !== null && value !== undefined && (
      <div className="mt-2 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5 w-fit">
        <CheckCircle className="w-3 h-3 text-[#088A61]" />
        <span className="text-xs font-bold text-[#088A61]">Entered: {value} points</span>
      </div>
    )}
  </div>
);

// ── Years input (enter raw years uncapped, score is capped at maxPts) ──────────
interface YearsScoreProps {
  label: string;
  maxPts: number;
  years: number | null;
  onChange: (v: number | null) => void;
  readOnly?: boolean;
  hint?: string;
}
const YearsScore: React.FC<YearsScoreProps> = ({ label, maxPts, years, onChange, readOnly = false, hint }) => {
  const score = years === null ? null : Math.min(maxPts, Math.max(0, years));
  const capped = years !== null && years > maxPts;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider">{label}</p>
        <span className="text-[10px] font-bold text-[#088A61] bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
          Max {maxPts} pts
        </span>
      </div>
      {hint && <p className="text-[10px] text-gray-400 italic mb-2">{hint}</p>}
      <div className="flex items-center gap-3">
        <input
          type="number"
          min={0}
          disabled={readOnly}
          value={years ?? ''}
          onChange={e => {
            const v = e.target.value === '' ? null : Math.max(0, Math.floor(Number(e.target.value)));
            onChange(v);
          }}
          className="w-28 px-3 py-2 border border-gray-200 rounded-lg text-sm font-bold text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#088A61]/30 focus:border-[#088A61] disabled:bg-gray-100 disabled:cursor-default"
          placeholder="0"
        />
        <span className="text-xs text-gray-400 font-medium">years in operation</span>
      </div>
      {score !== null && (
        <div className="mt-2 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5 w-fit">
          <CheckCircle className="w-3 h-3 text-[#088A61]" />
          <span className="text-xs font-bold text-[#088A61]">
            Score: {score} / {maxPts} pts{capped ? ` (capped — ${years} yrs entered)` : ''}
          </span>
        </div>
      )}
    </div>
  );
};

// ── Confirm Modal ─────────────────────────────────────────────────────────────

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'draft' | 'submit';
  onConfirmDraft: () => void;
  onConfirmSubmit: () => void;
  isSavingDraft: boolean;
  isSavingSubmit: boolean;
  journalName: string;
}
const ConfirmModal: React.FC<ConfirmModalProps> = ({ isOpen, onClose, mode, onConfirmDraft, onConfirmSubmit, isSavingDraft, isSavingSubmit, journalName }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={!isSavingDraft && !isSavingSubmit ? onClose : undefined} />
      <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className={`px-6 py-5 ${mode === 'submit' ? 'bg-gradient-to-r from-[#088A61] to-[#065f44]' : 'bg-gradient-to-r from-gray-700 to-gray-800'}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                {mode === 'submit' ? <Send className="w-5 h-5 text-white" /> : <Save className="w-5 h-5 text-white" />}
              </div>
              <div>
                <h3 className="text-base font-black text-white">
                  {mode === 'submit' ? 'Submit Assessment' : 'Save as Draft'}
                </h3>
                <p className="text-xs text-white/70 mt-0.5">
                  {mode === 'submit' ? 'This will finalise the journal evaluation' : 'Your progress will be saved'}
                </p>
              </div>
            </div>
            {!isSavingDraft && !isSavingSubmit && (
              <button onClick={onClose} className="text-white/60 hover:text-white transition-colors flex-shrink-0">
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="bg-gray-50 border border-gray-100 rounded-lg px-4 py-3">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-0.5">Journal</p>
            <p className="text-sm font-semibold text-gray-800 leading-snug">{journalName}</p>
          </div>

          {mode === 'submit' ? (
            <>
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700 font-medium leading-relaxed">
                  Submitting will finalise this evaluation. Ensure all scoring sections are complete before proceeding.
                </p>
              </div>
              <div className="flex flex-col gap-2 pt-1">
                <button onClick={onConfirmSubmit} disabled={isSavingSubmit || isSavingDraft}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-[#088A61] text-white rounded-xl text-sm font-bold hover:bg-[#067451] transition-colors shadow-md disabled:opacity-60 disabled:cursor-not-allowed">
                  {isSavingSubmit
                    ? <><div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />Submitting…</>
                    : <><Send className="w-4 h-4" />Submit Assessment</>}
                </button>
                <button onClick={onConfirmDraft} disabled={isSavingDraft || isSavingSubmit}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 border border-gray-200 text-gray-600 bg-white rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                  {isSavingDraft
                    ? <><div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />Saving…</>
                    : <><Save className="w-4 h-4" />Save as Draft Instead</>}
                </button>
                <button onClick={onClose} disabled={isSavingDraft || isSavingSubmit}
                  className="w-full px-5 py-2.5 text-gray-400 text-sm font-medium hover:text-gray-600 transition-colors disabled:opacity-60">
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
                <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-700 font-medium leading-relaxed">
                  Your form data will be saved as a draft. You can return and complete it at any time.
                </p>
              </div>
              <div className="flex items-center gap-3 pt-1">
                <button onClick={onClose} disabled={isSavingDraft}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 font-semibold hover:bg-gray-50 transition-colors disabled:opacity-60">
                  Cancel
                </button>
                <button onClick={onConfirmDraft} disabled={isSavingDraft}
                  className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 bg-gray-800 text-white rounded-xl text-sm font-bold hover:bg-gray-900 transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed">
                  {isSavingDraft
                    ? <><div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />Saving…</>
                    : <><Save className="w-4 h-4" />Save Draft</>}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Remark helper ─────────────────────────────────────────────────────────────

const getRemark = (total: number) => {
  if (total >= 80) return { label: 'Excellent',  color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-300' };
  if (total >= 70) return { label: 'Very Good',  color: 'text-blue-700',    bg: 'bg-blue-50 border-blue-300' };
  if (total >= 60) return { label: 'Eligible',   color: 'text-teal-700',    bg: 'bg-teal-50 border-teal-300' };
  if (total >= 50) return { label: 'Good',       color: 'text-amber-700',   bg: 'bg-amber-50 border-amber-300' };
  return               { label: 'Below Min.',   color: 'text-red-700',     bg: 'bg-red-50 border-red-300' };
};

// ── Main Component ────────────────────────────────────────────────────────────

const JournalApplicationEvaluationForm: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [assignment, setAssignment] = useState<JournalAssignmentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [scores, setScores] = useState<ScoreMap>(emptyScoreMap());

  // Raw years entered for Date of Commencement (uncapped; score is capped at 10)
  const [commencementYears, setCommencementYears] = useState<number | null>(null);

  // Editorial board sub-scores that combine into EDITORIAL_BOARD_QUALIFICATION (max 15)
  const [boardQual, setBoardQual]       = useState<number | null>(null); // 0 / 3 / 5
  const [boardMembers, setBoardMembers] = useState<number | null>(null); // 3 / 5
  const [boardSpread, setBoardSpread]   = useState<number | null>(null); // 2 / 3 / 5

  // IDs for update round-trips
  const [sectionId, setSectionId] = useState<string>(ZERO_GUID);
  const [itemIds, setItemIds] = useState<Record<string, string>>({});
  const [assessmentId, setAssessmentId] = useState<string>(ZERO_GUID);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [confirmModal, setConfirmModal] = useState<'draft' | 'submit' | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [savingSubmit, setSavingSubmit] = useState(false);

  // ── Load assignment ─────────────────────────────────────────────────────────

  const loadAssignment = React.useCallback(async () => {
    if (!assignmentId) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await journalAssessmentService.getJournalAssignment(assignmentId);
      if (res?.isSuccess && res.data) {
        const detail = res.data;
        setAssignment(detail);
        setIsSubmitted(detail.journalAssessment?.submitted === true);

        if (detail.journalAssessment) {
          const ja = detail.journalAssessment;
          setAssessmentId(ja.id ?? ZERO_GUID);

          const newScores = emptyScoreMap();
          const newItemIds: Record<string, string> = {};
          const newSelected: Record<string, string> = {};

          for (const sec of ja.sectionScores ?? []) {
            if (sec.section === SECTION_NUMBER) {
              setSectionId(sec.id ?? ZERO_GUID);
              for (const item of sec.items ?? []) {
                newScores[item.criteriaCode] = item.score;
                newItemIds[item.criteriaCode] = item.id;
                newSelected[item.criteriaCode] = item.selectedValue;
              }
            }
          }
          setScores(newScores);
          setItemIds(newItemIds);

          // Restore raw years entered (encoded in selectedValue, e.g. "20 years")
          const dcYears = parseInt(newSelected['DATE_COMMENCEMENT'] ?? '', 10);
          setCommencementYears(
            Number.isFinite(dcYears) ? dcYears : newScores['DATE_COMMENCEMENT'] ?? null
          );

          // Restore editorial board sub-scores (encoded as "Q:5 | M:5 | S:3")
          const ebSel = newSelected['EDITORIAL_BOARD_QUALIFICATION'] ?? '';
          const pick = (re: RegExp) => {
            const m = ebSel.match(re);
            return m ? Number(m[1]) : null;
          };
          setBoardQual(pick(/Q:(-?\d+)/));
          setBoardMembers(pick(/M:(-?\d+)/));
          setBoardSpread(pick(/S:(-?\d+)/));
        }
      } else {
        showNotification('error', { title: 'Error', message: res?.message || 'Failed to load assignment' });
      }
    } catch {
      showNotification('error', { title: 'Error', message: 'Failed to load assignment' });
    } finally {
      setLoading(false);
    }
  }, [assignmentId]);

  useEffect(() => { loadAssignment(); }, [loadAssignment]);

  // ── Score helpers ───────────────────────────────────────────────────────────

  const setScore = (code: string) => (value: number | null) =>
    setScores(prev => ({ ...prev, [code]: value }));

  // Date of Commencement — store raw years, but cap the score at 10 pts
  const setCommencement = (years: number | null) => {
    setCommencementYears(years);
    setScores(prev => ({
      ...prev,
      DATE_COMMENCEMENT: years === null ? null : Math.min(CRITERIA_MAX.DATE_COMMENCEMENT, Math.max(0, years)),
    }));
  };

  // Editorial board — combine qualification + membership + spread into a single /15 score
  const applyBoard = (q: number | null, m: number | null, s: number | null) => {
    const any = q !== null || m !== null || s !== null;
    setScores(prev => ({
      ...prev,
      EDITORIAL_BOARD_QUALIFICATION: any ? (q ?? 0) + (m ?? 0) + (s ?? 0) : null,
    }));
  };
  const setBoardQualScore = (v: number | null)    => { setBoardQual(v);    applyBoard(v, boardMembers, boardSpread); };
  const setBoardMembersScore = (v: number | null) => { setBoardMembers(v); applyBoard(boardQual, v, boardSpread); };
  const setBoardSpreadScore = (v: number | null)  => { setBoardSpread(v);  applyBoard(boardQual, boardMembers, v); };

  const grandTotal = Object.entries(scores).reduce(
    (sum, [, v]) => sum + (v ?? 0),
    0
  );

  // Normalise to 100 based on max raw total
  const normalised = Math.min(100, (grandTotal / totalMax) * 100);

  // ── Validate ────────────────────────────────────────────────────────────────

  const validateForSubmit = (): string[] => {
    const missing = Object.keys(CRITERIA_MAX)
      .filter(code => code !== 'EDITORIAL_BOARD_QUALIFICATION')
      .filter(code => scores[code] === null || scores[code] === undefined)
      .map(code => FIELD_LABELS[code] ?? code);
    // The board criterion is only complete once all three sub-scores are picked
    if (boardQual === null || boardMembers === null || boardSpread === null) {
      missing.push(FIELD_LABELS.EDITORIAL_BOARD_QUALIFICATION);
    }
    return missing;
  };

  // ── Build payload ───────────────────────────────────────────────────────────

  // Human-readable (and reload-parseable) value stored alongside each score
  const selectedValueFor = (code: string): string => {
    if (code === 'DATE_COMMENCEMENT') return `${commencementYears ?? scores[code]} years`;
    if (code === 'EDITORIAL_BOARD_QUALIFICATION') return `Q:${boardQual} | M:${boardMembers} | S:${boardSpread}`;
    return OPTION_LABELS[code]?.[scores[code]!] ?? String(scores[code]);
  };

  const buildPayload = (submit: boolean): CreateUpdateJournalAssessmentPayload => ({
    id: assessmentId,
    journalAssignmentId: assignmentId ?? ZERO_GUID,
    submit,
    sections: [
      {
        id: sectionId,
        section: SECTION_NUMBER,
        items: Object.keys(CRITERIA_MAX)
          .filter(code => scores[code] !== null)
          .map(code => ({
            id: itemIds[code] ?? ZERO_GUID,
            criteriaCode: code,
            selectedValue: selectedValueFor(code),
            score: scores[code]!,
            maxScore: CRITERIA_MAX[code],
          })),
      },
    ],
  });

  // ── Save / submit actions ───────────────────────────────────────────────────

  const executeSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const res = await journalAssessmentService.createUpdateJournalAssessment(buildPayload(false));
      if (res?.isSuccess) {
        showNotification('success', { title: 'Draft Saved', message: 'Your progress has been saved.' });
        setConfirmModal(null);
        await loadAssignment();
      } else {
        showNotification('error', { title: 'Error', message: res?.message || 'Failed to save draft.' });
      }
    } catch {
      showNotification('error', { title: 'Error', message: 'An unexpected error occurred.' });
    } finally {
      setSavingDraft(false);
    }
  };

  const executeSubmit = async () => {
    setSavingSubmit(true);
    try {
      const res = await journalAssessmentService.createUpdateJournalAssessment(buildPayload(true));
      if (res?.isSuccess) {
        showNotification('success', { title: 'Submitted', message: 'Journal evaluation submitted successfully.' });
        setConfirmModal(null);
        navigate(-1);
      } else {
        showNotification('error', { title: 'Error', message: res?.message || 'Failed to submit.' });
      }
    } catch {
      showNotification('error', { title: 'Error', message: 'An unexpected error occurred.' });
    } finally {
      setSavingSubmit(false);
    }
  };

  // ── Derived display values ──────────────────────────────────────────────────

  const sub = assignment?.submission;
  const journalName  = sub?.title ?? '—';
  const issn         = sub?.isbnNo ?? '—';
  const editorName   = sub?.authors
    ? `${sub.authors}${sub.positionOfJournalRepresentative ? ` (${sub.positionOfJournalRepresentative})` : ''}`
    : '—';
  const journalBody  = sub?.journalProfessionalAssociation ?? '—';
  const website      = sub?.publicationUrl ?? '—';

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) return (
    <div className="flex h-96 items-center justify-center">
      <div className="w-10 h-10 border-4 border-gray-100 border-t-[#088A61] rounded-full animate-spin" />
    </div>
  );

  const remarkData = getRemark(normalised);

  return (
    <div className="w-full max-w-6xl mx-auto px-4 pb-28">
      <style>{JOURNAL_APP_EVAL_PRINT_STYLES}</style>

      <ConfirmModal
        isOpen={confirmModal !== null}
        onClose={() => !savingDraft && !savingSubmit && setConfirmModal(null)}
        mode={confirmModal ?? 'draft'}
        onConfirmDraft={executeSaveDraft}
        onConfirmSubmit={executeSubmit}
        isSavingDraft={savingDraft}
        isSavingSubmit={savingSubmit}
        journalName={journalName}
      />

      <div className="print:hidden">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-700 font-medium transition-colors mb-6 group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          Back to Journals
        </button>

        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#088A61] to-[#065f44] px-8 py-7 shadow-lg">
          <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/5" />
          <div className="absolute -bottom-10 -right-2 w-56 h-56 rounded-full bg-white/5" />
          <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-emerald-200 uppercase tracking-widest mb-0.5">
                  TETFund — Journal Publications Sub-Committee
                </p>
                <h1 className="text-xl sm:text-2xl font-black text-white leading-tight">
                  Journal Application Evaluation
                </h1>
                <p className="text-sm font-bold text-white/80 mt-0.5">First Level of Funding Support</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => window.print()}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors border border-white/20"
              >
                <Printer className="w-4 h-4" /> Print
              </button>
              {/* Live score badge */}
              <div className="bg-white/15 border border-white/20 rounded-xl px-4 py-3 text-center min-w-[90px]">
                <p className="text-[10px] font-black text-emerald-200 uppercase tracking-widest mb-0.5">Score</p>
                <p className="text-3xl font-black text-white">{grandTotal}</p>
                <p className="text-[10px] text-white/60 font-bold">/ {totalMax} raw</p>
              </div>
            </div>
          </div>

          <div className="relative mt-5 flex flex-wrap gap-3">
            {[
              { label: 'ISSN', value: issn },
              { label: 'Editor-in-Chief', value: editorName },
              { label: 'Journal Body', value: journalBody },
              ...(isSubmitted ? [{ label: 'Status', value: 'Submitted' }] : []),
            ].map(item => (
              <div key={item.label} className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-1.5 border border-white/15">
                <span className="text-[10px] font-bold text-emerald-300 uppercase">{item.label}</span>
                <span className="text-xs font-bold text-white">{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Submitted banner */}
      {isSubmitted && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-3 mb-4">
          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
          <p className="text-[11px] font-bold text-green-700 uppercase tracking-wide">
            This evaluation has been submitted and is now read-only.
          </p>
        </div>
      )}

      {/* Journal identification */}
      <div className="mb-5">
        <SectionCard title="Journal Identification">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ROField label="Name of Journal"                value={journalName} />
            <ROField label="Name of Professional Association" value={journalBody} />
            <ROField label="ISSN"                           value={issn} />
            <ROField label="Name of Editor-in-Chief"        value={editorName} />
            <ROField label="Website Address"                value={website} fullWidth />
          </div>
        </SectionCard>
      </div>

      {/* Scoring rubric note */}
      <SectionCard accent>
        <div className="space-y-1.5">
          <p className="text-[11px] font-black text-[#088A61] uppercase tracking-widest">Scoring Notes</p>
          <ul className="space-y-1 text-xs text-gray-600">
            <li>• Qualification of editorial board: ≥50% PhD = 5 pts, &lt;50% = 3 pts, no PhD = 0</li>
            <li>• Membership of Editorial Board: ≥5 = 5 pts, &lt;5 = 3 pts</li>
            <li>• Spread of board members: Local = 2 pts, National = 3 pts, International = 5 pts</li>
            <li>• Spread of Contributing Authors: Local = 5 pts, National = 7 pts, International = 10 pts</li>
            <li>• Indexing: ≥3 platforms = 15 pts, 2 platforms = 10 pts, 1 platform = 2 pts</li>
            <li>• Consistency of Publication: Based on frequency claimed in the Editorial Policy</li>
            <li className="font-bold text-[#088A61]">• Minimum score for eligibility: 60%</li>
          </ul>
        </div>
      </SectionCard>

      {/* ── Criteria cards ── */}
      <div className="mt-5 space-y-4">

        {/* 1. Date of Commencement */}
        <SectionCard title="Date of Commencement of the Journal (max 10 pts)">
          <YearsScore
            label="1 pt per year of publication, up to max of 10 pts"
            maxPts={10}
            years={commencementYears}
            onChange={setCommencement}
            readOnly={isSubmitted}
            hint="Enter the number of years in operation — you may enter more than 10 (e.g. 20); the score is capped at 10."
          />
        </SectionCard>

        {/* 2. No. of Copies Submitted */}
        <SectionCard title="No. of Copies of Journal Submitted (max 3 pts)">
          <NumericScore
            label="1 pt per volume submitted, up to max of 3 pts (at least 3 volumes)"
            maxPts={3}
            value={scores['COPIES_SUBMITTED']}
            onChange={setScore('COPIES_SUBMITTED')}
            readOnly={isSubmitted}
            hint="At least 3 volumes required. 1 pt per volume, max 3 pts."
          />
        </SectionCard>

        {/* 3. Identifiable Editorial Board */}
        <SectionCard title="Identifiable Editorial Board (5 pts)">
          <ScoreOptions
            label="Is the Editorial Board identifiable?"
            maxPts={5}
            selected={scores['EDITORIAL_BOARD_IDENTIFIABLE']}
            onChange={setScore('EDITORIAL_BOARD_IDENTIFIABLE')}
            readOnly={isSubmitted}
            options={[
              { label: 'Not Identified',  value: 0 },
              { label: 'Board Identified', value: 5 },
            ]}
          />
        </SectionCard>

        {/* 4. Qualification, Affiliation & Spread of Editorial Board Members */}
        <SectionCard title="Qualification, Affiliation & Spread of Editorial Board Members (max 15 pts)">
          <div className="space-y-5">
            <ScoreOptions
              label="Qualification (PhD ratio)"
              maxPts={5}
              selected={boardQual}
              onChange={setBoardQualScore}
              readOnly={isSubmitted}
              hint="≥50% with PhD = 5 pts, <50% = 3 pts, no PhD = 0"
              options={[
                { label: 'No PhD',   value: 0 },
                { label: '<50% PhD', value: 3 },
                { label: '≥50% PhD', value: 5 },
              ]}
            />
            <hr className="border-gray-100" />
            <ScoreOptions
              label="Membership size"
              maxPts={5}
              selected={boardMembers}
              onChange={setBoardMembersScore}
              readOnly={isSubmitted}
              hint="≥5 members = 5 pts, <5 members = 3 pts"
              options={[
                { label: '<5 members', value: 3 },
                { label: '≥5 members', value: 5 },
              ]}
            />
            <hr className="border-gray-100" />
            <ScoreOptions
              label="Spread of board members"
              maxPts={5}
              selected={boardSpread}
              onChange={setBoardSpreadScore}
              readOnly={isSubmitted}
              hint="Local = 2 pts, National = 3 pts, International = 5 pts"
              options={[
                { label: 'Local',         value: 2 },
                { label: 'National',      value: 3 },
                { label: 'International', value: 5 },
              ]}
            />
            <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              <span className="text-[10px] font-black text-[#088A61] uppercase tracking-wider">Combined sub-total</span>
              <span className="text-sm font-black text-[#088A61]">
                {scores['EDITORIAL_BOARD_QUALIFICATION'] ?? 0} / 15 pts
              </span>
            </div>
          </div>
        </SectionCard>

        {/* 5. Visibility of Editor-in-Chief */}
        <SectionCard title="Visibility of Editor-in-Chief (2 pts)">
          <ScoreOptions
            label="Visible on Google Scholar, Scopus, ResearchGate, etc."
            maxPts={2}
            selected={scores['EDITOR_IN_CHIEF_VISIBILITY']}
            onChange={setScore('EDITOR_IN_CHIEF_VISIBILITY')}
            readOnly={isSubmitted}
            options={[
              { label: 'Not visible',  value: 0 },
              { label: 'Visible',      value: 2 },
            ]}
          />
        </SectionCard>

        {/* 6. ISSN */}
        <SectionCard title="ISSN (max 10 pts)">
          <ScoreOptions
            label="Publication format"
            maxPts={10}
            selected={scores['ISSN_STATUS']}
            onChange={setScore('ISSN_STATUS')}
            readOnly={isSubmitted}
            hint="Print only = 5 pts, Print & Online = 10 pts"
            options={[
              { label: 'Print only',     value: 5 },
              { label: 'Print & Online', value: 10 },
            ]}
          />
        </SectionCard>

        {/* 7. Evidence of Peer Review Mechanism */}
        <SectionCard title="Evidence of Peer Review Mechanism (5 pts)">
          <ScoreOptions
            label="Peer review evidence present?"
            maxPts={5}
            selected={scores['PEER_REVIEW']}
            onChange={setScore('PEER_REVIEW')}
            readOnly={isSubmitted}
            options={[
              { label: 'No evidence',   value: 0 },
              { label: 'Has evidence',  value: 5 },
            ]}
          />
        </SectionCard>

        {/* 8. Evidence of Abstracting & Indexing */}
        <SectionCard title="Evidence of Abstracting & Indexing (max 15 pts)">
          <ScoreOptions
            label="Number of platforms (Elsevier, Scopus, PubMed, AJOL, etc.)"
            maxPts={15}
            selected={scores['INDEXING']}
            onChange={setScore('INDEXING')}
            readOnly={isSubmitted}
            hint="≥3 platforms = 15 pts, 2 platforms = 10 pts, 1 platform = 2 pts"
            options={[
              { label: '1 platform',   value: 2 },
              { label: '2 platforms',  value: 10 },
              { label: '≥3 platforms', value: 15 },
            ]}
          />
        </SectionCard>

        {/* 9. Consistency of Publication */}
        <SectionCard title="Consistency of Publication (max 10 pts)">
          <ScoreOptions
            label="Publication matches stated frequency in Editorial Policy"
            maxPts={10}
            selected={scores['PUBLICATION_CONSISTENCY']}
            onChange={setScore('PUBLICATION_CONSISTENCY')}
            readOnly={isSubmitted}
            hint="Score is based on adherence to the frequency claimed in the Editorial Policy."
            options={[
              { label: 'Inconsistent', value: 0 },
              { label: 'Partially consistent', value: 5 },
              { label: 'Fully consistent',     value: 10 },
            ]}
          />
        </SectionCard>

        {/* 10. Editorial Policy */}
        <SectionCard title="Editorial Policy (max 10 pts)">
          <ScoreOptions
            label="Does the journal have a documented Editorial Policy?"
            maxPts={10}
            selected={scores['EDITORIAL_POLICY']}
            onChange={setScore('EDITORIAL_POLICY')}
            readOnly={isSubmitted}
            options={[
              { label: 'No policy',      value: 0 },
              { label: 'Has policy',     value: 10 },
            ]}
          />
        </SectionCard>

        {/* 11. Spread of Contributing Authors */}
        <SectionCard title="Spread of Contributing Authors (max 10 pts)">
          <ScoreOptions
            label="Geographic spread of authors"
            maxPts={10}
            selected={scores['AUTHOR_SPREAD']}
            onChange={setScore('AUTHOR_SPREAD')}
            readOnly={isSubmitted}
            hint="Local = 5 pts, National = 7 pts, International = 10 pts"
            options={[
              { label: 'Local',         value: 5 },
              { label: 'National',      value: 7 },
              { label: 'International', value: 10 },
            ]}
          />
        </SectionCard>

        {/* 12. DOI */}
        <SectionCard title="Evidence of Digital Object Identifier — DOI (5 pts)">
          <ScoreOptions
            label="Does the journal have DOI?"
            maxPts={5}
            selected={scores['DOI']}
            onChange={setScore('DOI')}
            readOnly={isSubmitted}
            options={[
              { label: 'No DOI',   value: 0 },
              { label: 'Has DOI',  value: 5 },
            ]}
          />
        </SectionCard>

      </div>

      {/* ── Results summary ── */}
      <div className="mt-6 space-y-4">
        <SectionCard title="Score Summary" accent>
          <div className="overflow-hidden rounded-lg border border-emerald-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-emerald-50 border-b border-emerald-200">
                  <th className="text-left px-4 py-2.5 text-[10px] font-black text-[#088A61] uppercase tracking-wider">Criterion</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-black text-[#088A61] uppercase tracking-wider">Score</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-black text-[#088A61] uppercase tracking-wider">Max</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {Object.entries(CRITERIA_MAX).map(([code, max]) => (
                  <tr key={code} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-2.5 text-xs text-gray-600">{FIELD_LABELS[code]}</td>
                    <td className="px-4 py-2.5 text-right font-black text-[#088A61] text-sm">
                      {scores[code] !== null && scores[code] !== undefined ? scores[code] : <span className="text-gray-300 font-medium">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right text-xs font-bold text-gray-400">{max}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 border-t-2 border-gray-200">
                  <td className="px-4 py-3 text-xs font-black text-gray-700 uppercase tracking-wider">Total Raw Score</td>
                  <td className="px-4 py-3 text-right font-black text-[#088A61] text-lg">{grandTotal}</td>
                  <td className="px-4 py-3 text-right text-sm font-black text-gray-500">{totalMax}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </SectionCard>

        {/* Grand total block */}
        <div className={`rounded-xl border-2 ${remarkData.bg} p-5`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${remarkData.color}`}>
                Total Score (out of {totalMax})
              </p>
              <p className="text-xs text-gray-500">
                Minimum 60% of raw score required for eligibility
              </p>
            </div>
            <div className="text-right">
              <p className={`text-4xl font-black ${remarkData.color}`}>{grandTotal}<span className="text-lg">/{totalMax}</span></p>
              <p className={`text-sm font-black uppercase tracking-wider mt-1 ${remarkData.color}`}>{remarkData.label}</p>
            </div>
          </div>
        </div>

        {/* Eligibility note */}
        <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
          <CheckCircle className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-gray-500 leading-relaxed">
            The Sub-committee has recommended that a score of <strong>60%</strong> be the minimum for a Journal to be eligible for the support.
            A score of {Math.round(totalMax * 0.6)} or above ({totalMax} × 60%) qualifies.
          </p>
        </div>
      </div>

      {/* ── Sticky footer actions ── */}
      {!isSubmitted && (
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-100 px-4 sm:px-6 py-3 flex items-center justify-between gap-3 shadow-lg">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider hidden sm:block">Score</span>
            <span className={`text-lg font-black ${grandTotal >= Math.round(totalMax * 0.6) ? 'text-emerald-700' : 'text-red-500'}`}>{grandTotal}</span>
            <span className="text-xs text-gray-400 font-bold">/ {totalMax}</span>
            <span className={`hidden sm:inline text-[10px] font-black px-2 py-0.5 rounded-full ${
              grandTotal >= Math.round(totalMax * 0.6) ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'
            }`}>
              {remarkData.label}
            </span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setConfirmModal('draft')}
              disabled={savingDraft || savingSubmit}
              className="flex items-center gap-2 px-4 py-2 border border-[#088A61] text-[#088A61] bg-white rounded-lg text-sm font-bold hover:bg-emerald-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              <span className="hidden sm:inline">Save</span> Draft
            </button>
            <button
              onClick={() => {
                const missing = validateForSubmit();
                if (missing.length > 0) {
                  showNotification('error', {
                    title: 'Incomplete Evaluation',
                    message: `Please score the following: ${missing.join(', ')}.`,
                  });
                  return;
                }
                setConfirmModal('submit');
              }}
              disabled={savingDraft || savingSubmit}
              className="flex items-center gap-2 px-4 sm:px-6 py-2 bg-[#088A61] text-white rounded-lg text-sm font-bold hover:bg-[#067451] transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" />
              <span className="hidden sm:inline">Submit</span> Assessment
            </button>
          </div>
        </div>
      )}
      </div>

      <JournalAppEvalPrintView
        journalName={journalName}
        journalBody={journalBody}
        issn={issn}
        editorName={editorName}
        website={website}
        scores={scores}
        commencementYears={commencementYears}
        boardQual={boardQual}
        boardMembers={boardMembers}
        boardSpread={boardSpread}
        grandTotal={grandTotal}
        normalised={normalised}
        remarkLabel={remarkData.label}
      />
    </div>
  );
};

// ── Print-only view ──────────────────────────────────────────────────────────

interface JournalAppEvalPrintViewProps {
  journalName: string;
  journalBody: string;
  issn: string;
  editorName: string;
  website: string;
  scores: ScoreMap;
  commencementYears: number | null;
  boardQual: number | null;
  boardMembers: number | null;
  boardSpread: number | null;
  grandTotal: number;
  normalised: number;
  remarkLabel: string;
}

const sectionHeaderJAE = (label: string) => (
  <h3 style={{ fontSize: '10.5pt', fontWeight: 800, textTransform: 'uppercase', borderBottom: '1pt solid #000', paddingBottom: '3pt', marginBottom: '8pt' }}>
    {label}
  </h3>
);

/** Print-only label of what was picked for a criterion */
const selectedLabelForPrint = (code: string, scores: ScoreMap, years: number | null, q: number | null, m: number | null, s: number | null): string => {
  const v = scores[code];
  if (v === null || v === undefined) return '—';
  if (code === 'DATE_COMMENCEMENT') {
    const ys = years ?? v;
    return `${ys} year${ys === 1 ? '' : 's'} of publication${years !== null && years > 10 ? ' (capped at 10 pts)' : ''}`;
  }
  if (code === 'EDITORIAL_BOARD_QUALIFICATION') {
    const parts: string[] = [];
    if (q !== null) parts.push(`Qualification ${q} pt${q === 1 ? '' : 's'}`);
    if (m !== null) parts.push(`Membership ${m} pt${m === 1 ? '' : 's'}`);
    if (s !== null) parts.push(`Spread ${s} pt${s === 1 ? '' : 's'}`);
    return parts.join(' · ');
  }
  return OPTION_LABELS[code]?.[v] ?? String(v);
};

const JournalAppEvalPrintView: React.FC<JournalAppEvalPrintViewProps> = (p) => {
  const eligibilityThreshold = Math.round(totalMax * 0.6);
  const isEligible = p.grandTotal >= eligibilityThreshold;

  const orderedCriteria: Array<{ code: string; label: string }> = [
    { code: 'DATE_COMMENCEMENT',           label: '1. Date of Commencement of the Journal' },
    { code: 'COPIES_SUBMITTED',            label: '2. No. of Copies of Journal Submitted' },
    { code: 'EDITORIAL_BOARD_IDENTIFIABLE',label: '3. Identifiable Editorial Board' },
    { code: 'EDITORIAL_BOARD_QUALIFICATION',label: '4. Qualification, Affiliation & Spread of Editorial Board Members' },
    { code: 'EDITOR_IN_CHIEF_VISIBILITY',  label: '5. Visibility of Editor-in-Chief' },
    { code: 'ISSN_STATUS',                 label: '6. ISSN' },
    { code: 'PEER_REVIEW',                 label: '7. Evidence of Peer Review Mechanism' },
    { code: 'INDEXING',                    label: '8. Evidence of Abstracting & Indexing' },
    { code: 'PUBLICATION_CONSISTENCY',     label: '9. Consistency of Publication' },
    { code: 'EDITORIAL_POLICY',            label: '10. Editorial Policy' },
    { code: 'AUTHOR_SPREAD',               label: '11. Spread of Contributing Authors' },
    { code: 'DOI',                         label: '12. Evidence of Digital Object Identifier (DOI)' },
  ];

  return (
    <div className="hidden print:block jae-print-doc" style={{ fontFamily: 'Georgia, "Times New Roman", serif', color: '#000', background: 'white', fontSize: '10pt', lineHeight: 1.4 }}>

      {/* ── HEADER ── */}
      <div className="jae-print-section" style={{ borderBottom: '2pt solid #000', paddingBottom: '8pt', marginBottom: '12pt', textAlign: 'center' }}>
        <p style={{ fontSize: '9pt', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, margin: 0 }}>
          TETFund — Journal Publications Sub-Committee
        </p>
        <h1 style={{ fontSize: '18pt', fontWeight: 800, margin: '4pt 0 2pt 0', lineHeight: 1.1 }}>
          Journal Application Evaluation
        </h1>
        <p style={{ fontSize: '10pt', margin: '0 0 4pt 0', fontStyle: 'italic' }}>First Level of Funding Support</p>
        <p style={{ fontSize: '9.5pt', marginTop: '4pt', marginBottom: 0 }}>
          {p.journalName} &nbsp;·&nbsp; <strong>ISSN:</strong> {p.issn}
        </p>
      </div>

      {/* ── JOURNAL IDENTIFICATION ── */}
      <div className="jae-print-section" style={{ marginBottom: '14pt' }}>
        {sectionHeaderJAE('Journal Identification')}
        <table style={{ width: '100%', fontSize: '9.5pt', borderCollapse: 'collapse' }}>
          <tbody>
            <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top', width: '36%' }}>Name of Journal</td><td style={{ padding: '2pt 0' }}>{p.journalName}</td></tr>
            <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top' }}>Name of Professional Association</td><td style={{ padding: '2pt 0' }}>{p.journalBody}</td></tr>
            <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top' }}>ISSN</td><td style={{ padding: '2pt 0' }}>{p.issn}</td></tr>
            <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top' }}>Name of Editor-in-Chief</td><td style={{ padding: '2pt 0' }}>{p.editorName}</td></tr>
            <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top' }}>Website Address</td><td style={{ padding: '2pt 0' }}>{p.website}</td></tr>
          </tbody>
        </table>
      </div>

      {/* ── SCORE SUMMARY TABLE ── */}
      <div className="jae-print-section" style={{ marginBottom: '14pt' }}>
        {sectionHeaderJAE('Score Summary')}
        <table style={{ width: '100%', fontSize: '9.5pt', borderCollapse: 'collapse', border: '0.75pt solid #000' }}>
          <thead>
            <tr style={{ borderBottom: '0.75pt solid #000' }}>
              <th style={{ textAlign: 'left', padding: '4pt 6pt', fontWeight: 800, fontSize: '8pt', textTransform: 'uppercase' }}>Criterion</th>
              <th style={{ textAlign: 'right', padding: '4pt 6pt', fontWeight: 800, fontSize: '8pt', textTransform: 'uppercase', width: '12%' }}>Score</th>
              <th style={{ textAlign: 'right', padding: '4pt 6pt', fontWeight: 800, fontSize: '8pt', textTransform: 'uppercase', width: '12%' }}>Max</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(CRITERIA_MAX).map(([code, max]) => (
              <tr key={code} style={{ borderBottom: '0.25pt solid #999' }}>
                <td style={{ padding: '3pt 6pt' }}>{FIELD_LABELS[code]}</td>
                <td style={{ textAlign: 'right', padding: '3pt 6pt', fontWeight: 700 }}>
                  {p.scores[code] !== null && p.scores[code] !== undefined ? p.scores[code] : '—'}
                </td>
                <td style={{ textAlign: 'right', padding: '3pt 6pt' }}>{max}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: '1pt solid #000' }}>
              <td style={{ padding: '4pt 6pt', fontWeight: 800, fontSize: '10pt', textTransform: 'uppercase' }}>Total Raw Score ({p.remarkLabel})</td>
              <td style={{ textAlign: 'right', padding: '4pt 6pt', fontWeight: 800, fontSize: '12pt' }}>{p.grandTotal}</td>
              <td style={{ textAlign: 'right', padding: '4pt 6pt', fontWeight: 800, fontSize: '10pt' }}>{totalMax}</td>
            </tr>
          </tfoot>
        </table>
        <p style={{ fontSize: '8.5pt', marginTop: '4pt', fontStyle: 'italic' }}>
          Eligibility threshold: {eligibilityThreshold} / {totalMax} ({totalMax} × 60%). This application is {isEligible ? 'ELIGIBLE' : 'NOT ELIGIBLE'} based on the current score.
        </p>
      </div>

      {/* ── DETAILED CRITERIA ── */}
      <div style={{ marginBottom: '14pt' }}>
        {sectionHeaderJAE('Detailed Criteria & Selections')}
        {orderedCriteria.map(({ code, label }) => {
          const score = p.scores[code];
          const max = CRITERIA_MAX[code];
          return (
            <div key={code} className="jae-print-section" style={{ marginBottom: '8pt', borderLeft: '2pt solid #000', paddingLeft: '8pt' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <h4 style={{ fontSize: '10pt', fontWeight: 800, margin: '0 0 2pt 0', flex: 1 }}>{label}</h4>
                <span style={{ fontSize: '10pt', fontWeight: 800, marginLeft: '8pt' }}>
                  {score !== null && score !== undefined ? score : '—'} / {max}
                </span>
              </div>
              <p style={{ fontSize: '9pt', margin: '0 0 4pt 0', fontStyle: 'italic' }}>
                Selected: {selectedLabelForPrint(code, p.scores, p.commencementYears, p.boardQual, p.boardMembers, p.boardSpread)}
              </p>
              {code === 'EDITORIAL_BOARD_QUALIFICATION' && (
                <table style={{ width: '100%', fontSize: '9pt', borderCollapse: 'collapse', marginTop: '2pt' }}>
                  <tbody>
                    <tr><td style={{ padding: '1pt 6pt 1pt 0', width: '50%' }}>Qualification (PhD ratio)</td><td style={{ padding: '1pt 0', textAlign: 'right', fontWeight: 700 }}>{p.boardQual ?? '—'} / 5</td></tr>
                    <tr><td style={{ padding: '1pt 6pt 1pt 0' }}>Membership size</td><td style={{ padding: '1pt 0', textAlign: 'right', fontWeight: 700 }}>{p.boardMembers ?? '—'} / 5</td></tr>
                    <tr><td style={{ padding: '1pt 6pt 1pt 0' }}>Spread of board members</td><td style={{ padding: '1pt 0', textAlign: 'right', fontWeight: 700 }}>{p.boardSpread ?? '—'} / 5</td></tr>
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>

      {/* ── GRAND TOTAL & ELIGIBILITY ── */}
      <div className="jae-print-section" style={{ marginBottom: '14pt', border: '1pt solid #000', padding: '8pt 10pt' }}>
        {sectionHeaderJAE('Eligibility Outcome')}
        <table style={{ width: '100%', fontSize: '10pt' }}>
          <tbody>
            <tr>
              <td style={{ fontWeight: 700, padding: '2pt 0' }}>Raw Score</td>
              <td style={{ textAlign: 'right', padding: '2pt 0', fontWeight: 800, fontSize: '13pt' }}>{p.grandTotal} / {totalMax}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 700, padding: '2pt 0' }}>Normalised (%)</td>
              <td style={{ textAlign: 'right', padding: '2pt 0' }}>{p.normalised.toFixed(1)}%</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 700, padding: '2pt 0' }}>Remark</td>
              <td style={{ textAlign: 'right', padding: '2pt 0' }}>{p.remarkLabel}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 700, padding: '2pt 0' }}>Eligibility</td>
              <td style={{ textAlign: 'right', padding: '2pt 0', fontWeight: 800 }}>{isEligible ? 'ELIGIBLE' : 'NOT ELIGIBLE'}</td>
            </tr>
          </tbody>
        </table>
        <p style={{ fontSize: '8.5pt', marginTop: '4pt', fontStyle: 'italic' }}>
          The Sub-committee has recommended that a score of 60% be the minimum for a Journal to be eligible for the support. A score of {eligibilityThreshold} or above ({totalMax} × 60%) qualifies.
        </p>
      </div>

      {/* ── SIGNATURE ── */}
      <div className="jae-print-section" style={{ marginTop: '18pt', paddingTop: '8pt', borderTop: '1pt solid #000' }}>
        <table style={{ width: '100%', fontSize: '9.5pt' }}>
          <tbody>
            <tr>
              <td style={{ verticalAlign: 'top', paddingRight: '12pt', width: '60%' }}>
                <p style={{ margin: 0 }}>Assessor's Signature: ____________________________</p>
                <p style={{ margin: '6pt 0 0 0' }}>Name: ____________________________</p>
                <p style={{ margin: '6pt 0 0 0' }}>Date: ____________________________</p>
              </td>
              <td style={{ verticalAlign: 'top', borderLeft: '0.5pt solid #999', paddingLeft: '12pt' }}>
                <p style={{ margin: 0 }}><strong>Final Score:</strong> {p.grandTotal} / {totalMax}</p>
                <p style={{ margin: '4pt 0 0 0' }}><strong>Outcome:</strong> {isEligible ? 'ELIGIBLE' : 'NOT ELIGIBLE'}</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

    </div>
  );
};

export default JournalApplicationEvaluationForm;